/**
 * The character sheet.
 *
 * Read-only for now, and honest about it: the engine moves hit points, XP and
 * everything derived, so a sheet that let a player type over them would be a
 * second source of truth for numbers the server has already decided.
 *
 * **THAC0 and the saving throws come from the server.** They are lookups into
 * two tables that a level-up changes, and computing them here would be the
 * kind of second copy this project keeps refusing — with the added trap that
 * both are *roll-under or meet-or-beat* in ways another edition's conventions
 * get backwards.
 */

import { orderedAbilities } from './abilities.js';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api, ApiError } from '../api.js';
import { optionLabel } from './options.js';
import { BackToTableLink } from '../pages/SheetLink.js';
import { AppearanceSection } from './AppearanceSection.js';
import {
  InventorySection,
  toWritableInventory,
  type ArmourClassBreakdown,
  type InventoryItem,
  type Load,
} from './InventorySection.js';
import { SkillsSection, type HeldSkill, type SkillDescriptor } from './SkillsSection.js';
import { ThievingSection, type ThievingView } from './ThievingSection.js';

interface SheetResponse {
  character: {
    id: string;
    name: string;
    race: string;
    alignment: string;
    /**
     * What the character IS — Tables 2.5 and 2.6, and the gender field.
     *
     * All four are null on every character made before 2026-08-30, which is
     * why each is rendered only when it is there: a blank beside a label reads
     * as a broken read, and an invented figure reads as a fact.
     */
    gender?: string | null;
    age?: number | null;
    heightIn?: number | null;
    weightLb?: number | null;
    /** Written once and read back, never regenerated. The player may edit it. */
    description?: string | null;
    ownerId?: string | null;
    /** Set for a henchman: the player character who pays for their gear. */
    patronCharacterId?: string | null;
    stats: Record<string, number>;
    hitPointsCurrent: number;
    hitPointsMax: number;
    nonLethalDamage?: number;
    inventory?: InventoryItem[];
    skills?: {
      nonCombatSkills?: HeldSkill[];
      bonusSkill?: string;
      thievingSkills?: Record<string, number>;
      /** Chapter 5 — what they trained with, and how. */
      weaponSkills?: { name: string; tier: string }[];
      combatMethods?: { name: string; category: string }[];
      ambidextrous?: boolean;
      specialisedIn?: string;
    };
    deathStatus?: string;
    /** The Irritation rash, when the character has one: what it has cost so far. */
    rash?: { sinceDay: number; lostCharisma: number; lostDexterity: number } | null;
  };
  classes: { class: string; level: number; xp: number }[];
  /**
   * The table this character sits at, when it sits at one.
   *
   * The sheet opens in a **new tab** from the play surface, so there is no
   * history to go back through — without this the only route back to the game
   * is the lobby.
   */
  campaignId?: string;
  derived?: {
    thac0: number | null;
    savingThrows: Record<string, number> | null;
    best: string | null;
    armourClass?: ArmourClassBreakdown;
    load?: Load;
    /** Coin carried, spoken in gold/silver/copper and as the copper total it is stored as. */
    purse?: { copper: number; text: string };
    /** What one of each pack row sells for, aligned by index; null where nothing prices it. */
    itemNames?: Record<string, string>;
    lights?: Record<string, { lit: boolean; spent: boolean; minutesRemaining?: number }>;
    skillPoints?: { spent: number; available: number };
    combatSkills?: {
      spent: number;
      available: number;
      unskilledPenalty: { unrelated: number; sameGroup: number };
    };
    /** Which class groups this character satisfies a skill restriction with. */
    classGroups?: string[];
    thieving?: ThievingView;
  };
}

/** One part of the sheet: a bordered card, so each is visibly one thing. */
const CARD = 'rounded-md border border-neutral-300 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-950';
const CARD_TITLE = 'text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400';
/** A boxed figure: a small label over a large value, the way a paper sheet prints one. */
const STAT_BOX = 'flex flex-col rounded-md border border-neutral-300 px-2 py-1 dark:border-neutral-700';
const STAT_LABEL = 'text-xs text-neutral-500 dark:text-neutral-400';
const STAT_VALUE = 'text-lg font-semibold';

export function CharacterSheetPage() {
  const { characterId } = useParams();
  const [sheet, setSheet] = useState<SheetResponse | null>(null);
  // **Two different failures, shown differently.** `error` is "this sheet could not be loaded at all" and
  // is the only thing that replaces the page. `notice` is a change that was refused or failed (a skill past
  // the points, a save that did not go through): the sheet stays on screen, unchanged, with the reason above it.
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inventoryNotice, setInventoryNotice] = useState<string | null>(null);
  // A refused skill change shows beside the Skills control as well as at the top: the top is off screen down there.
  const [skillsNotice, setSkillsNotice] = useState<string | null>(null);
  const [thievingNotice, setThievingNotice] = useState<string | null>(null);
  // The catalogue is the server's — what a skill costs, what it is rolled
  // against and who may take it are all rules. An empty one renders the list
  // and no way to add to it, rather than a hand-written menu.
  const [skillCatalogue, setSkillCatalogue] = useState<readonly SkillDescriptor[]>([]);

  /**
   * Writes the whole list and takes the server's answer back wholesale.
   *
   * Not a local update with a fire-and-forget PATCH: the write **re-derives
   * armour class and encumbrance**, so the response is the only thing that
   * knows what the sheet now says. Guessing here would show an AC the server
   * disagrees with until the next reload.
   */
  /**
   * Writes the whole skill list, and takes the server's answer back.
   *
   * Same shape as the inventory and for the same reason: the write re-derives
   * what was spent and can **refuse** the loadout outright — a skill the class
   * may not take, a tier past the budget — and the refusal is the only thing
   * that knows.
   */
  const saveSkills = (nonCombatSkills: readonly HeldSkill[]) => {
    if (!characterId) return;
    setNotice(null);
    setSkillsNotice(null);
    void api
      .patch<SheetResponse>(`/api/characters/${characterId}`, {
        skills: { ...(sheet?.character.skills ?? {}), nonCombatSkills },
      })
      .then(() => api.get<SheetResponse>(`/api/characters/${characterId}`))
      .then((body) => body && setSheet(body))
      .catch((e: unknown) => {
        const message = e instanceof Error && e.message ? e.message : 'could not save the skills';
        setNotice(message);
        setSkillsNotice(message);
      });
  };

  /**
   * Writes the whole allocation and takes the server's answer back.
   *
   * Like the inventory and the skills: the write **re-derives every chance**
   * from it and can refuse the allocation outright — over the total, over the
   * per-skill cap, a skill the class does not have — and the response is the
   * only thing that knows.
   */
  const saveThieving = (thievingSkills: Record<string, number>) => {
    if (!characterId) return;
    setNotice(null);
    setThievingNotice(null);
    void api
      .patch<SheetResponse>(`/api/characters/${characterId}`, {
        skills: { ...(sheet?.character.skills ?? {}), thievingSkills },
      })
      .then(() => api.get<SheetResponse>(`/api/characters/${characterId}`))
      .then((body) => body && setSheet(body))
      .catch((e: unknown) => {
        const message = e instanceof Error && e.message ? e.message : 'could not spend the points';
        setNotice(message);
        setThievingNotice(message);
      });
  };

  /**
   * The player's own words about their character, written back.
   *
   * ⚠ **The creation wizard promises this exists** — "you can edit it on the
   * sheet, but nothing rewrites it" — and until now the sheet showed neither
   * the description nor any of the body, so the promise was false and the one
   * field the DM reads on every beat was unreachable after creation.
   *
   * **Age is not here**, and it is the one of the four that cannot be: its
   * ability adjustment was applied to the scores when the character was made,
   * so the sheet no longer holds anything that says what an age already cost.
   * The server refuses it by name for the same reason.
   */
  const saveSelf = (patch: { gender?: string; description?: string }) => {
    if (!characterId) return;
    setNotice(null);
    void api
      .patch<SheetResponse>(`/api/characters/${characterId}`, patch)
      .then(() => api.get<SheetResponse>(`/api/characters/${characterId}`))
      .then((body) => body && setSheet(body))
      .catch((e: unknown) =>
        setNotice(e instanceof Error && e.message ? e.message : 'could not save that'),
      );
  };

  const saveInventory = (inventory: readonly InventoryItem[]) => {
    if (!characterId) return;
    void api
      .patch<SheetResponse>(`/api/characters/${characterId}`, {
        inventory: toWritableInventory(inventory),
      })
      .then(() => api.get<SheetResponse>(`/api/characters/${characterId}`))
      .then((body) => body && setSheet(body))
      // **The server's own words, beside the inventory** — not the page-wide
      // alert, which would replace the whole sheet. A refusal here is usually
      // "that is on the price list: buy it", which the player can act on.
      .catch((e: unknown) =>
        setInventoryNotice(
          e instanceof ApiError && e.message ? e.message : 'could not save the inventory',
        ),
      );
    setInventoryNotice(null);
  };


  // **Its own request, never folded into `saveInventory`.** The server
  // refuses to mix `lightItem` with an ordinary inventory edit — it stamps
  // the light from the table's own clock, a read-modify-write against the
  // *stored* pack rather than whatever this tab happened to have loaded.
  const lightItem = (name: string) => {
    if (!characterId) return;
    setNotice(null);
    void api
      .patch<SheetResponse>(`/api/characters/${characterId}`, { lightItem: name })
      .then(() => api.get<SheetResponse>(`/api/characters/${characterId}`))
      .then((body) => body && setSheet(body))
      .catch((e: unknown) =>
        setNotice(e instanceof ApiError ? e.message : `could not light ${name}`),
      );
  };

  // Its own request, for the same reason `lightItem` is: the server works out how much has burned from the
  // table's clock and the stored pack, and keeps what is left so the light can be lit again later.
  const putOutItem = (name: string) => {
    if (!characterId) return;
    setNotice(null);
    void api
      .patch<SheetResponse>(`/api/characters/${characterId}`, { putOutItem: name })
      .then(() => api.get<SheetResponse>(`/api/characters/${characterId}`))
      .then((body) => body && setSheet(body))
      .catch((e: unknown) =>
        setNotice(e instanceof ApiError ? e.message : `could not put out ${name}`),
      );
  };

  useEffect(() => {
    void api
      .get<{ skills?: SkillDescriptor[] }>('/api/character-options')
      .then((body) => setSkillCatalogue(body.skills ?? []))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!characterId) return;
    void api
      .get<SheetResponse>(`/api/characters/${characterId}`)
      // 404 is the same answer someone else's character gets: whether an id
      // exists is not information a stranger is entitled to.
      .catch(() => setError('character not found'))
      .then((body) => body && setSheet(body));
  }, [characterId]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">
        {error}
      </p>
    );
  }
  if (!sheet) return <p className="text-sm">Loading…</p>;

  const { character, classes, derived } = sheet;

  return (
    <div className="space-y-4">
      <BackToTableLink campaignId={sheet.campaignId} />
      {notice && (
        <p
          role="alert"
          className="sticky top-2 z-10 flex items-start gap-2 rounded-md border border-amber-500/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 shadow dark:bg-amber-950 dark:text-amber-200"
        >
          <span className="flex-1">{notice}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="text-xs underline"
            aria-label="Dismiss this message"
          >
            dismiss
          </button>
        </p>
      )}
      <header className={`${CARD} space-y-1`}>
        <h1 className="text-xl font-semibold">{character.name}</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {optionLabel(character.race)} ·{' '}
          {classes.map((c) => `${optionLabel(c.class)} ${c.level}`).join(' / ')} ·{' '}
          {optionLabel(character.alignment)}
          {character.deathStatus && character.deathStatus !== 'alive' && (
            <span className="ml-2 font-medium text-amber-800 dark:text-amber-300">
              {character.deathStatus}
            </span>
          )}
          {character.rash && (
            <span
              className="ml-2 font-medium text-amber-800 dark:text-amber-300"
              title="From Irritation. Cure Disease or Dispel Magic ends it and gives back what it took."
            >
              rash
              {character.rash.lostCharisma + character.rash.lostDexterity > 0 &&
                ` (−${[
                  character.rash.lostCharisma > 0 ? `${character.rash.lostCharisma} Cha` : '',
                  character.rash.lostDexterity > 0 ? `${character.rash.lostDexterity} Dex` : '',
                ]
                  .filter(Boolean)
                  .join(', −')})`}
            </span>
          )}
        </p>
      </header>

      {/* **The classic sheet's top block**: the six abilities down the left as boxed scores, the numbers a fight
          reads (hit points, armour class, THAC0, saves) in boxes on the right. Everything below is a bordered
          card of its own, so each part of the sheet is visibly one thing. */}
      <div className="grid gap-4 md:grid-cols-[11rem_minmax(0,1fr)]">
        <section aria-label="Abilities" className={`${CARD} space-y-2`}>
          <h2 className={CARD_TITLE}>Abilities</h2>
          <ul className="space-y-1.5 text-sm tabular-nums">
            {orderedAbilities(character.stats).map(([ability, score]) => (
              <li
                key={ability}
                className="flex items-baseline justify-between rounded-md border border-neutral-300 px-2 py-1 dark:border-neutral-700"
              >
                <span className="text-neutral-500 dark:text-neutral-400">{optionLabel(ability)}</span>
                <span className="text-lg font-semibold">{score}</span>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          <section aria-label="Combat" className={`${CARD} space-y-2`}>
            <h2 className={CARD_TITLE}>Combat</h2>
            <ul className="grid grid-cols-2 gap-2 text-sm tabular-nums sm:grid-cols-4">
              <li className={STAT_BOX}>
                <span className={STAT_LABEL}>HP</span>
                <span className={STAT_VALUE}>
                  {character.hitPointsCurrent}/{character.hitPointsMax}
                </span>
              </li>
              {/* **Derived, never stored.** The `armour_class` column was removed when AC became a lookup over
                  the equipped inventory and Dexterity; this line once kept reading it off the character row, a
                  field that no longer existed, so it rendered as *nothing at all* while the inventory panel
                  printed the real figure from `derived`. Blank rather than wrong, which is why it survived. */}
              {derived?.armourClass && (
                <li className={STAT_BOX} title="descending: lower is better, 10 is unarmoured">
                  <span className={STAT_LABEL}>AC</span>
                  <span className={STAT_VALUE}>{derived.armourClass.armourClass}</span>
                </li>
              )}
              {derived?.thac0 !== null && derived?.thac0 !== undefined && (
                <li className={STAT_BOX} title="hit if d20 + modifiers ≥ THAC0 − target AC">
                  <span className={STAT_LABEL}>THAC0</span>
                  <span className={STAT_VALUE}>{derived.thac0}</span>
                </li>
              )}
              {(character.nonLethalDamage ?? 0) > 0 && (
                // Kept apart from hit points on the sheet as well as in the engine: reaching zero this way is
                // unconsciousness, not death.
                <li
                  className={STAT_BOX}
                  title="brawling damage — reaching zero this way knocks you out rather than killing you"
                >
                  <span className={STAT_LABEL}>Non-lethal</span>
                  <span className={STAT_VALUE}>{character.nonLethalDamage}</span>
                </li>
              )}
              {/* **Derived text from the server**, like AC: the purse is stored as a copper total and spoken by
                  `describeCoin`, so no client copy of the exchange rates can drift from the book's. */}
              {derived?.purse && (
                <li className={`${STAT_BOX} col-span-2`} title="coin carried — 1 lb per 50 coins">
                  <span className={STAT_LABEL}>Purse</span>
                  <span id="purse" className="text-sm font-medium">
                    {derived.purse.text}
                  </span>
                </li>
              )}
            </ul>
          </section>
          {derived?.savingThrows && (
            <section aria-label="Saving throws" className={`${CARD} space-y-2`}>
              <h2 className={CARD_TITLE}>Saving throws</h2>
              {/* Lower is better, and it says so: roll d20 and meet or beat. */}
              <p className="text-xs text-neutral-500 dark:text-neutral-400">roll d20, meet or beat — lower is better</p>
              <ul className="grid grid-cols-2 gap-2 text-sm tabular-nums sm:grid-cols-5">
                {Object.entries(derived.savingThrows).map(([category, target]) => (
                  <li key={category} className={STAT_BOX}>
                    <span className={STAT_LABEL}>{optionLabel(category)}</span>
                    <span className={STAT_VALUE}>{target}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      <div className={CARD}>
        <AppearanceSection
          character={character}
          readOnly={character.deathStatus !== undefined && character.deathStatus !== 'alive'}
          onChange={saveSelf}
        />
      </div>

      {/* ⚠ **What the round now charges them for, which nothing showed
          before.** The unskilled penalty is applied silently in the attack
          roll, so a player four worse to hit than they expect has to be able
          to read why — and until 2026-08-30 it was applied nowhere at all. */}
      {(derived?.combatSkills || (character.skills?.weaponSkills ?? []).length > 0) && (
        <section className={`${CARD} space-y-1`}>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Combat training
          </h2>
          {derived?.combatSkills && (
            // ⚠ **Worded to match the wizard, after a live drive-through read
            // this and the wizard's copy for the same fact side by side.** The
            // wizard's "untrained, this class is at −2 to hit" reads as a
            // statement about what an *untrained* weapon costs; the sheet had
            // dropped both qualifiers and read as though the penalty were
            // already being paid on the weapon just trained.
            <>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                {derived.combatSkills.spent} combat skill {derived.combatSkills.spent === 1 ? 'point' : 'points'} spent
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Combat skill points are exchanged for skills when a character is made; any not exchanged then are
                lost, and the choice is permanent. More points are earned at higher levels.
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                Untrained, this class is at {derived.combatSkills.unskilledPenalty.unrelated} to hit (
                {derived.combatSkills.unskilledPenalty.sameGroup} with a weapon in the same group as one it is
                trained in).
              </p>
            </>
          )}
          <ul className="flex flex-wrap gap-3 text-sm">
            {(character.skills?.weaponSkills ?? []).map((skill) => (
              <li key={`${skill.tier}:${skill.name}`}>
                {optionLabel(skill.name)}{' '}
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {optionLabel(skill.tier)}
                </span>
              </li>
            ))}
            {(character.skills?.combatMethods ?? []).map((method) => (
              <li key={`${method.category}:${method.name}`}>
                {optionLabel(method.name)}{' '}
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {method.category} method
                </span>
              </li>
            ))}
            {character.skills?.ambidextrous && <li>Ambidextrous</li>}
            {character.skills?.specialisedIn && (
              <li>
                Specialised in {optionLabel(character.skills.specialisedIn)}{' '}
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  +1 to hit, +2 damage
                </span>
              </li>
            )}
          </ul>
        </section>
      )}

      <div className={CARD}>
      <InventorySection
        items={character.inventory ?? []}
        {...(derived?.armourClass ? { armourClass: derived.armourClass } : {})}
        {...(derived?.load ? { load: derived.load } : {})}
        onChange={saveInventory}
        onLight={lightItem}
        onPutOut={putOutItem}
        {...(derived?.lights ? { lights: derived.lights } : {})}
        {...(derived?.itemNames ? { names: derived.itemNames } : {})}
        {...(inventoryNotice ? { notice: inventoryNotice } : {})}
        readOnly={character.deathStatus !== undefined && character.deathStatus !== 'alive'}
      />
      </div>

      {/* **There is no shop on the sheet** (Maz, 2026-10-20): gear is bought from a shopkeeper standing where the
          party is — see the table's "Shops here". */}

      <div className={CARD}>
      <SkillsSection
        held={character.skills?.nonCombatSkills ?? []}
        catalogue={skillCatalogue}
        {...(character.skills?.bonusSkill ? { bonusSkill: character.skills.bonusSkill } : {})}
        spent={derived?.skillPoints?.spent ?? 0}
        {...(derived?.skillPoints ? { available: derived.skillPoints.available } : {})}
        {...(derived?.classGroups ? { classGroups: derived.classGroups } : {})}
        onChange={saveSkills}
        notice={skillsNotice}
        readOnly={character.deathStatus !== undefined && character.deathStatus !== 'alive'}
      />
      </div>

      {derived?.thieving && (
        <div className={CARD}>
          <ThievingSection
            thieving={derived.thieving}
            onChange={saveThieving}
            notice={thievingNotice}
            readOnly={character.deathStatus !== undefined && character.deathStatus !== 'alive'}
          />
        </div>
      )}

      <section className={`${CARD} space-y-1`}>
        <h2 className={CARD_TITLE}>Experience</h2>
        <ul className="flex flex-wrap gap-3 text-sm tabular-nums">
          {classes.map((c) => (
            <li key={c.class}>
              <span className="text-neutral-500 dark:text-neutral-400">{optionLabel(c.class)} </span>
              {c.xp}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
